import type { Updater } from '@tanstack/vue-table'
import type { ClassValue } from 'clsx'
import { clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Resolves a TanStack updater (value or function) against its previous state.
 * Used for controlled table state slices (sorting, filters, visibility, …).
 */
export function valueUpdater<T>(updaterOrValue: Updater<T>, previous: T): T {
  return typeof updaterOrValue === 'function'
    ? (updaterOrValue as (prev: T) => T)(previous)
    : updaterOrValue
}

/**
 * Returns the best text color (white or black) to contrast with the given background color.
 * Supports Hex, RGB(A), and OKLCH.
 */
export function getContrastColor(color: string): 'white' | 'black' {
  if (!color) return 'white'

  const trimmed = color.trim().toLowerCase()

  // Handle OKLCH: oklch(L C H) or oklch(L% C H)
  if (trimmed.startsWith('oklch')) {
    const match = trimmed.match(/oklch\s*\(\s*([\d.]+%?)/)
    if (match) {
      const lStr = match[1]
      let l = Number.parseFloat(lStr)
      if (lStr.endsWith('%')) {
        l = l / 100
      }
      // OKLCH Lightness (L) is a very accurate measure of perceived brightness.
      // Usually, L > 0.6 is safe for black text.
      return l > 0.6 ? 'black' : 'white'
    }
  }

  // Handle RGB/RGBA: rgb(r, g, b) or rgba(r, g, b, a)
  if (trimmed.startsWith('rgb')) {
    const match = trimmed.match(/rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/)
    if (match) {
      const r = Number.parseInt(match[1])
      const g = Number.parseInt(match[2])
      const b = Number.parseInt(match[3])
      const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255
      return luminance > 0.5 ? 'black' : 'white'
    }
  }

  // Handle Hex
  let hex = trimmed.replace('#', '')

  // Handle shorthand hex (e.g. "FFF" -> "FFFFFF")
  if (hex.length === 3) {
    hex = [...hex].map((char) => char + char).join('')
  }

  const r = Number.parseInt(hex.slice(0, 2), 16)
  const g = Number.parseInt(hex.slice(2, 4), 16)
  const b = Number.parseInt(hex.slice(4, 6), 16)

  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return 'white'

  const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255
  return luminance > 0.5 ? 'black' : 'white'
}

/**
 * Safely parses the error message from an HTTP exception response.
 */
export function getHttpErrorMessage(
  response: unknown,
  fallback: string,
): string {
  if (
    response &&
    typeof response === 'object' &&
    'data' in response &&
    typeof response.data === 'string'
  ) {
    let parsed: unknown
    try {
      parsed = JSON.parse(response.data)
    } catch {
      parsed = undefined
    }
    if (
      parsed &&
      typeof parsed === 'object' &&
      'message' in parsed &&
      typeof parsed.message === 'string'
    ) {
      return parsed.message
    }
  }
  return fallback
}

/**
 * Safely removes query parameters from the browser URL without triggering a page reload.
 */
export function removeQueryParam(key: string | string[]): void {
  if (typeof window === 'undefined') return
  try {
    const url = new URL(window.location.href)
    const keys = Array.isArray(key) ? key : [key]
    let changed = false
    for (const k of keys) {
      if (url.searchParams.has(k)) {
        url.searchParams.delete(k)
        changed = true
      }
    }
    if (changed) {
      window.history.replaceState({}, '', url.pathname + url.search)
    }
  } catch {
    // Ignore URL parsing errors in non-standard environments
  }
}
